import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import UserAvatar from '../../components/common/UserAvatar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { TablePagination } from '../../components/ui/table-pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, Search, CheckCircle, XCircle, Clock, FileText, User, MapPin,
  Star, Briefcase, Phone, Mail, Eye, Filter,
  Download, Calendar, DollarSign, Award, AlertTriangle, Package
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AdminProviders() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  
  // Pagination & Filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [onlineFilter, setOnlineFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchProviders = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (onlineFilter !== 'all') params.append('online', onlineFilter);
      if (typeFilter !== 'all') params.append('provider_type', typeFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/admin/all-providers?${params}`);
      setProviders(response.data.providers || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, statusFilter, onlineFilter, typeFilter, searchTerm]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Debounced search - reset to page 1 when search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page !== 1) setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, page]);

  const handleVerify = async (providerId, status) => {
    setProcessingId(providerId);
    try {
      await axios.put(`${API}/admin/providers/${providerId}/verify?status=${status}`);
      toast.success(`Provider ${status}!`);
      setSelectedProvider(null);
      fetchProviders();
    } catch (error) {
      toast.error('Failed to update verification');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      verified: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return styles[status] || 'bg-stone-100 text-stone-600';
  };

  const getStatusIcon = (status) => {
    if (status === 'verified') return <CheckCircle className="w-4 h-4" />;
    if (status === 'pending') return <Clock className="w-4 h-4" />;
    if (status === 'rejected') return <XCircle className="w-4 h-4" />;
    return null;
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />

      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center md:hidden"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Provider Management</h1>
              <p className="text-slate-500">View and manage all service providers</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{total}</p>
                <p className="text-xs text-slate-500">Total Providers</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {providers.filter(p => p.verification_status === 'verified').length}
                </p>
                <p className="text-xs text-slate-500">Verified</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {providers.filter(p => p.verification_status === 'pending').length}
                </p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {providers.filter(p => p.is_online).length}
                </p>
                <p className="text-xs text-slate-500">Online Now</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-col gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
                data-testid="provider-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(1); }}>
                <SelectTrigger className="h-10 flex-1 min-w-[120px]" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={onlineFilter} onValueChange={(value) => { setOnlineFilter(value); setPage(1); }}>
                <SelectTrigger className="h-10 flex-1 min-w-[120px]" data-testid="online-filter">
                  <SelectValue placeholder="All Availability" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Availability</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={(value) => { setTypeFilter(value); setPage(1); }}>
                <SelectTrigger className="h-10 flex-1 min-w-[100px]" data-testid="type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Providers List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
            </div>
          ) : providers.length === 0 ? (
            <div className="p-12 text-center">
              <User className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-slate-500">No providers found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Provider</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Contact</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Rating</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Jobs</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Online</th>
                      <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {providers.map((provider) => (
                      <tr key={provider.id} className="hover:bg-stone-50" data-testid={`provider-row-${provider.user_id}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <UserAvatar 
                              src={provider.user?.profile_image} 
                              name={provider.user?.name} 
                              role="provider"
                              size="md"
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900">{provider.user?.name || 'Provider'}</p>
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${provider.provider_type === 'company' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                                  {provider.provider_type === 'company' ? 'Company' : 'Individual'}
                                </span>
                              </div>
                              <p className="text-xs text-slate-400">
                                {provider.services?.length || 0} services • {provider.coverage_areas?.length || 0} areas
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700">{provider.user?.email}</p>
                          <p className="text-xs text-slate-500">{provider.user?.phone || '-'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                            <span className="font-medium text-slate-900">{provider.rating?.toFixed(1) || '0.0'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-slate-700">{provider.total_jobs || 0}</p>
                          <p className="text-xs text-slate-500">{provider.completion_rate || 0}% completion</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(provider.verification_status)}`}>
                            {getStatusIcon(provider.verification_status)}
                            {provider.verification_status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            provider.is_online ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'
                          }`}>
                            {provider.is_online ? 'Online' : 'Offline'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedProvider(provider)}
                            data-testid={`view-provider-${provider.user_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <TablePagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            </>
          )}
        </div>

        {/* Provider Detail Modal */}
        <Dialog open={!!selectedProvider} onOpenChange={() => setSelectedProvider(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-[Poppins]">Provider Details</DialogTitle>
            </DialogHeader>
            
            {selectedProvider && (
              <Tabs defaultValue="profile" className="mt-4">
                <TabsList className="mb-4">
                  <TabsTrigger value="profile">Profile</TabsTrigger>
                  <TabsTrigger value="services">Services</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="stats">Statistics</TabsTrigger>
                </TabsList>

                {/* Profile Tab */}
                <TabsContent value="profile">
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-xl">
                      <UserAvatar 
                        src={selectedProvider.user?.profile_image} 
                        name={selectedProvider.user?.name} 
                        role="provider"
                        size="xl"
                      />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-slate-900">{selectedProvider.user?.name}</h3>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {selectedProvider.user?.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {selectedProvider.user?.phone || 'No phone'}
                          </span>
                        </div>
                        <div className="mt-2">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(selectedProvider.verification_status)}`}>
                            {getStatusIcon(selectedProvider.verification_status)}
                            {selectedProvider.verification_status}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 border rounded-xl">
                        <Label className="text-slate-500">Services Offered</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedProvider.services?.length > 0 ? (
                            selectedProvider.services.map((s, i) => {
                              const label = typeof s === 'string'
                                ? s
                                : (s?.service_name || s?.name || s?.category_name || 'Service');
                              return (
                                <span key={s?.id || `service-${i}-${label}`} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg">
                                  {label}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-slate-400 text-sm">No services listed</span>
                          )}
                        </div>
                      </div>
                      <div className="p-4 border rounded-xl">
                        <Label className="text-slate-500">Coverage Areas</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedProvider.coverage_areas?.length > 0 ? (
                            selectedProvider.coverage_areas.map((area, i) => {
                              const label = typeof area === 'string'
                                ? area
                                : (area?.name || area?.city || area?.area || 'Area');
                              return (
                                <span key={`area-${i}-${label}`} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-lg flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {label}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-slate-400 text-sm">No areas listed</span>
                          )}
                        </div>
                        {(selectedProvider.service_city || selectedProvider.service_province || selectedProvider.service_barangay) && (
                          <div className="mt-3 pt-3 border-t border-slate-100" data-testid="provider-base-location">
                            <Label className="text-slate-500 text-xs">Base Service Location</Label>
                            <p className="mt-1 text-sm text-slate-700 flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-amber-500" />
                              {[selectedProvider.service_barangay, selectedProvider.service_city, selectedProvider.service_province].filter(Boolean).join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedProvider.bio && (
                      <div className="p-4 border rounded-xl">
                        <Label className="text-slate-500">Bio</Label>
                        <p className="mt-2 text-slate-700">{selectedProvider.bio}</p>
                      </div>
                    )}

                    {/* Addresses */}
                    <div className="p-4 border rounded-xl">
                      <Label className="text-slate-500">Saved Addresses</Label>
                      {selectedProvider.addresses?.length > 0 ? (
                        <div className="space-y-2 mt-2">
                          {selectedProvider.addresses.map((addr, idx) => (
                            <div key={addr.id || `addr-${addr.label || idx}-${(addr.full_address || '').substring(0, 15)}`} className="p-3 bg-stone-50 rounded-lg">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-slate-900 text-sm">{addr.label || `Address ${idx + 1}`}</p>
                                  <p className="text-sm text-slate-600">{addr.full_address || addr.address}</p>
                                  {(addr.barangay || addr.city || addr.province) && (
                                    <p className="text-xs text-slate-500">
                                      {[addr.barangay, addr.city, addr.province].filter(Boolean).join(', ')}
                                    </p>
                                  )}
                                </div>
                                {addr.is_default && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Default</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (selectedProvider.service_barangay || selectedProvider.service_city || selectedProvider.service_province) ? (
                        <div className="p-3 bg-stone-50 rounded-lg mt-2" data-testid="provider-address-fallback">
                          <p className="font-medium text-slate-900 text-sm">Base Service Location</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {[selectedProvider.service_barangay, selectedProvider.service_city, selectedProvider.service_province].filter(Boolean).join(', ')}
                          </p>
                          <p className="text-[11px] text-slate-400 mt-1">Providers use a base location & coverage areas instead of saved addresses.</p>
                        </div>
                      ) : (
                        <p className="text-slate-400 text-sm mt-2">No addresses saved</p>
                      )}
                    </div>

                    {/* Verification Actions */}
                    {selectedProvider.verification_status === 'pending' && (
                      <div className="flex gap-3 pt-4 border-t">
                        <Button
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => handleVerify(selectedProvider.user_id, 'verified')}
                          disabled={processingId === selectedProvider.user_id}
                          data-testid="approve-btn"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve Provider
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => handleVerify(selectedProvider.user_id, 'rejected')}
                          disabled={processingId === selectedProvider.user_id}
                          data-testid="reject-btn"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    )}

                    {selectedProvider.verification_status === 'verified' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">This provider is verified and can accept jobs</span>
                        </div>
                      </div>
                    )}

                    {selectedProvider.verification_status === 'rejected' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-center gap-2 text-red-700">
                          <XCircle className="w-5 h-5" />
                          <span className="font-medium">This provider was rejected</span>
                        </div>
                        <Button
                          className="mt-3 bg-green-600 hover:bg-green-700"
                          onClick={() => handleVerify(selectedProvider.user_id, 'verified')}
                          disabled={processingId === selectedProvider.user_id}
                        >
                          Re-verify Provider
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Services Tab */}
                <TabsContent value="services">
                  <ProviderServices providerId={selectedProvider.user_id} />
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="documents">
                  <ProviderDocuments providerId={selectedProvider.user_id} />
                </TabsContent>

                {/* Stats Tab */}
                <TabsContent value="stats">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-amber-50 rounded-xl text-center">
                        <div className="flex items-center justify-center gap-1 text-amber-600 mb-2">
                          <Star className="w-6 h-6 fill-amber-500" />
                        </div>
                        <p className="text-3xl font-bold text-amber-700">{selectedProvider.rating?.toFixed(1) || '0.0'}</p>
                        <p className="text-sm text-amber-600">Average Rating</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-xl text-center">
                        <div className="flex items-center justify-center gap-1 text-blue-600 mb-2">
                          <Briefcase className="w-6 h-6" />
                        </div>
                        <p className="text-3xl font-bold text-blue-700">{selectedProvider.total_jobs || 0}</p>
                        <p className="text-sm text-blue-600">Total Jobs</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-xl text-center">
                        <div className="flex items-center justify-center gap-1 text-green-600 mb-2">
                          <CheckCircle className="w-6 h-6" />
                        </div>
                        <p className="text-3xl font-bold text-green-700">{selectedProvider.completion_rate || 0}%</p>
                        <p className="text-sm text-green-600">Completion Rate</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-xl text-center">
                        <div className="flex items-center justify-center gap-1 text-purple-600 mb-2">
                          <Calendar className="w-6 h-6" />
                        </div>
                        <p className="text-3xl font-bold text-purple-700">
                          {selectedProvider.created_at 
                            ? new Date(selectedProvider.created_at).toLocaleDateString()
                            : '-'
                          }
                        </p>
                        <p className="text-sm text-purple-600">Joined</p>
                      </div>
                    </div>

                    <div className="p-4 border rounded-xl">
                      <Label className="text-slate-500">Skills</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedProvider.skills?.length > 0 ? (
                          selectedProvider.skills.map((skill, i) => (
                            <span key={`skill-${skill}`} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-lg">
                              {skill}
                            </span>
                          ))
                        ) : (
                          <span className="text-slate-400 text-sm">No skills listed</span>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

// Provider Services component
function ProviderServices({ providerId }) {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/admin/providers/${providerId}/services`);
        setServices(response.data || []);
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchServices();
  }, [providerId]);

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto"></div>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="p-8 text-center border-2 border-dashed border-stone-200 rounded-xl">
        <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
        <p className="text-slate-500">No services listed</p>
        <p className="text-sm text-slate-400 mt-1">Provider has not added any services yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {services.map((svc) => (
        <div key={svc.id} className="flex items-start gap-4 p-4 border rounded-xl">
          <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium text-slate-900">{svc.name}</p>
                <p className="text-sm text-slate-500 mt-0.5">{svc.description}</p>
              </div>
              <span className="text-amber-600 font-bold whitespace-nowrap">₱{svc.price?.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{svc.category_name || 'Service'}</span>
              {svc.duration_hours && <span>Duration: {svc.duration_hours}h</span>}
              <span className={`px-2 py-0.5 rounded ${svc.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {svc.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Separate component for provider documents
function ProviderDocuments({ providerId }) {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingDocId, setProcessingDocId] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/admin/providers/${providerId}/documents`);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [providerId]);

  const handleApproveDoc = async (docId) => {
    setProcessingDocId(docId);
    try {
      await axios.put(`${API}/admin/providers/${providerId}/documents/${docId}?status=approved`);
      toast.success('Document approved!');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to approve document');
    } finally {
      setProcessingDocId(null);
    }
  };

  const openRejectDialog = (doc) => {
    setSelectedDoc(doc);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const handleRejectDoc = async () => {
    if (!selectedDoc) return;
    setProcessingDocId(selectedDoc.id);
    try {
      const params = new URLSearchParams({ status: 'rejected' });
      if (rejectionReason.trim()) {
        params.append('rejection_reason', rejectionReason.trim());
      }
      await axios.put(`${API}/admin/providers/${providerId}/documents/${selectedDoc.id}?${params}`);
      toast.success('Document rejected');
      setShowRejectDialog(false);
      setSelectedDoc(null);
      setRejectionReason('');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to reject document');
    } finally {
      setProcessingDocId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto"></div>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="p-8 text-center border-2 border-dashed border-stone-200 rounded-xl">
        <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
        <p className="text-slate-500">No documents uploaded</p>
        <p className="text-sm text-slate-400 mt-1">Provider has not submitted verification documents</p>
      </div>
    );
  }

  const documentTypes = {
    government_id: { label: 'Government ID', icon: User },
    id: { label: 'ID Document', icon: User },
    selfie: { label: 'Selfie', icon: User },
    nbi_clearance: { label: 'NBI Clearance', icon: FileText },
    barangay_clearance: { label: 'Barangay Clearance', icon: MapPin },
    proof_of_address: { label: 'Proof of Address', icon: MapPin },
    tesda_certificate: { label: 'TESDA Certificate', icon: Award },
    dti: { label: 'DTI Registration', icon: FileText },
    business_permit: { label: 'Business Permit', icon: FileText },
    bir: { label: 'BIR Registration', icon: FileText },
    other: { label: 'Other Document', icon: FileText },
  };

  // Get the file URL for viewing documents with auth token
  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    const token = localStorage.getItem('token');
    // Use the API endpoint for downloading files with auth token
    return `${API}/files/${filePath}?auth=${token}`;
  };

  return (
    <>
      <div className="space-y-3">
        {documents.map((doc) => {
          const docType = documentTypes[doc.document_type] || documentTypes.other;
          const DocIcon = docType.icon;
          const fileUrl = getFileUrl(doc.file_path);
          const isPending = doc.status === 'pending';
          const isApproved = doc.status === 'approved';
          const isRejected = doc.status === 'rejected';
          
          return (
            <div key={doc.id} className="p-4 border rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DocIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{docType.label}</p>
                      <p className="text-sm text-slate-500 truncate">{doc.original_filename}</p>
                      <p className="text-xs text-slate-400">
                        Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                      isApproved ? 'bg-green-100 text-green-700' :
                      isRejected ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {doc.status}
                    </span>
                  </div>
                  
                  {/* Rejection reason display */}
                  {isRejected && doc.rejection_reason && (
                    <div className="mt-2 p-2 bg-red-50 rounded-lg">
                      <p className="text-xs text-red-700">
                        <span className="font-medium">Rejection Reason:</span> {doc.rejection_reason}
                      </p>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 mt-3">
                    {fileUrl && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={fileUrl} target="_blank" rel="noopener noreferrer" data-testid={`view-doc-${doc.id}`}>
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </a>
                      </Button>
                    )}
                    
                    {/* Approve/Reject buttons - show for pending or rejected docs */}
                    {(isPending || isRejected) && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleApproveDoc(doc.id)}
                        disabled={processingDocId === doc.id}
                        data-testid={`approve-doc-${doc.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    )}
                    
                    {(isPending || isApproved) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => openRejectDialog(doc)}
                        disabled={processingDocId === doc.id}
                        data-testid={`reject-doc-${doc.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Please provide a reason for rejecting this document. This will help the provider understand what they need to fix.
            </p>
            <div>
              <Label>Rejection Reason</Label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Document is blurry, expired, or does not match the required type..."
                className="w-full h-24 mt-1.5 border border-stone-200 rounded-lg px-3 py-2 text-sm resize-none"
                data-testid="rejection-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowRejectDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleRejectDoc}
                disabled={processingDocId === selectedDoc?.id}
                data-testid="confirm-reject-btn"
              >
                {processingDocId === selectedDoc?.id ? 'Rejecting...' : 'Reject Document'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
